/**
 * Captions above a tunnel log: "Inclination" at the left and "Direction" at
 * the right, each with a short arrow, always on the same line.
 *
 * The inclination arrow is tilted by I about its own midpoint (clockwise on
 * screen for a falling tunnel, anticlockwise for a rising one), so however
 * steep the tunnel the arrow stays beside its caption instead of swinging
 * into it. The arrow carries the sense, so the value is printed unsigned.
 */

export const HEADER_FONT_SIZE = 10;
const ARROW_LENGTH = 18;
const HEAD_LENGTH = 6;
const HEAD_HALF_WIDTH = 2.5;
const GAP = 6;

/** Arrow from `from` to `to`: a line in the given style ending in a filled head. */
export function drawArrow({ Path, from, to, style }) {
  const back = from.subtract(to).normalize();
  const base = to.add(back.multiply(HEAD_LENGTH));
  const side = back.rotate(90).multiply(HEAD_HALF_WIDTH);
  const shaft = new Path.Line(from, base);
  shaft.style = style;
  const head = new Path({
    segments: [to, base.add(side), base.subtract(side)],
    closed: true,
  });
  head.style = style;
  head.fillColor = style.strokeColor || "black";
  return [shaft, head];
}

/**
 * Endpoints of the inclination arrow centred on `centre`: horizontal for
 * I = 0, turned by I degrees (positive = falling = clockwise on screen).
 */
export function inclinationArrowEnds({ Point, centre, inclination }) {
  const half = new Point(ARROW_LENGTH / 2, 0).rotate(Number(inclination) || 0);
  return { from: centre.subtract(half), to: centre.add(half) };
}

export function drawTunnelHeader({ Path, Point, PointText, bounds, direction, inclination, style }) {
  const baseline = bounds.top - 24;
  // Visual middle of the caption line, where the arrows sit.
  const mid = baseline - HEADER_FONT_SIZE * 0.35;
  const caption = (x, content, justification) =>
    new PointText({
      point: [x, baseline],
      content,
      justification,
      fillColor: "black",
      fontSize: HEADER_FONT_SIZE,
    });

  const inclValue = Number(inclination) || 0;
  const inclCaption = caption(bounds.left, "Inclination", "left");
  const centre = new Point(inclCaption.bounds.right + GAP + ARROW_LENGTH / 2, mid);
  const { from, to } = inclinationArrowEnds({ Point, centre, inclination: inclValue });
  drawArrow({ Path, from, to, style });
  const inclText = caption(centre.x + ARROW_LENGTH / 2 + GAP, Math.abs(inclValue) + "\xB0", "left");

  const dirTo = new Point(bounds.right, mid);
  const dirFrom = dirTo.subtract(new Point(ARROW_LENGTH, 0));
  const dirCaption = caption(dirFrom.x - GAP, "Direction N" + direction + "\xB0", "right");
  const dirItems = [...drawArrow({ Path, from: dirFrom, to: dirTo, style }), dirCaption];
  // Both captions stay on one line. On a log too short to right-align the
  // direction clear of the inclination, it follows the inclination instead.
  const inclRight = Math.max(inclText.bounds.right, from.x, to.x);
  const overlap = inclRight + 3 * GAP - dirCaption.bounds.left;
  if (overlap > 0) {
    dirItems.forEach((item) => item.translate(new Point(overlap, 0)));
  }
}
